import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { ICarrierAccount } from "@redotech/redo-model/carrier-account/carrier-account";
import { ThirdPartyBillingAccount } from "@redotech/redo-model/outbound-labels/third-party-billing-accounts";
import { createContext, memo, ReactNode, useMemo } from "react";

export interface CarrierAccountsContextValue {
  merchantCarrierAccounts: ICarrierAccount[];
  redoChildCarrierAccounts: ICarrierAccount[];
  thirdPartyBillingAccounts: ThirdPartyBillingAccount[];
  getCarrierAccountName: (carrierAccountId: string) => string | undefined;
  getThirdPartyBillingAccountName: (
    thirdPartyBillingAccountId: string,
  ) => string | undefined;
}

export const CarrierAccountsContext = createContext<
  CarrierAccountsContextValue | null | undefined
>(undefined);

export const CarrierAccountsProvider = memo(function CarrierAccountsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const carrierAccountsLoad = useLoad(
    async () => rpcClient.getCarrierAccounts(null),
    [rpcClient],
  );

  const thirdPartyBillingAccountsLoad = useLoad(
    async () => rpcClient.getThirdPartyBillingAccounts(null),
    [rpcClient],
  );

  const value = useMemo<CarrierAccountsContextValue | null>(() => {
    if (!carrierAccountsLoad.value) {
      return null;
    }

    const { merchantCarrierAccounts, redoChildCarrierAccounts } =
      carrierAccountsLoad.value;

    // Build a map of carrier account ID to name
    const carrierAccountIdToName = new Map<string, string>();
    for (const account of [
      ...merchantCarrierAccounts,
      ...redoChildCarrierAccounts,
    ]) {
      // Use carrierAccountId for easypost accounts, _id for other accounts
      const accountId = account.carrierAccountId ?? account._id.toString();
      carrierAccountIdToName.set(accountId, account.name);
    }

    const thirdPartyBillingAccounts =
      thirdPartyBillingAccountsLoad.value?.accounts ?? [];
    const thirdPartyBillingAccountIdToName = new Map<string, string>();
    for (const account of thirdPartyBillingAccounts) {
      thirdPartyBillingAccountIdToName.set(
        account._id.toString(),
        account.accountName,
      );
    }

    return {
      merchantCarrierAccounts,
      redoChildCarrierAccounts,
      thirdPartyBillingAccounts,
      getCarrierAccountName: (carrierAccountId: string) =>
        carrierAccountIdToName.get(carrierAccountId),
      getThirdPartyBillingAccountName: (thirdPartyBillingAccountId: string) =>
        thirdPartyBillingAccountIdToName.get(thirdPartyBillingAccountId),
    };
  }, [carrierAccountsLoad.value, thirdPartyBillingAccountsLoad.value]);

  return (
    <CarrierAccountsContext.Provider value={value}>
      {children}
    </CarrierAccountsContext.Provider>
  );
});
